import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Scale } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { Skeleton } from '../../../shared/components/Skeleton';
import { StatusBadge } from './PropostasComerciaisPage';
import { useProposta } from '../hooks';
import { formatCurrency } from '../utils/format';

/**
 * Listagem de versões (US-COM-006, task 15.5). Cronológico decrescente,
 * marca a `current_version_id` como vigente e as demais como
 * substituídas (RN-COM-008 + RN-COM-027). Permite selecionar duas para
 * comparar via `PropostaVersaoComparacaoPage`.
 */
export default function PropostaVersoesPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: proposta, isLoading, isError } = useProposta(id);

  const [selA, setSelA] = useState<string>('');
  const [selB, setSelB] = useState<string>('');

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError || !proposta) return <Alert variant="error">Proposta não encontrada.</Alert>;

  const versoes = [...(proposta.versions ?? [])].sort(
    (a, b) => (b.createdAt > a.createdAt ? 1 : -1),
  );

  function compararSelecionadas() {
    if (selA && selB && selA !== selB && id) {
      navigate(`/propostas/${id}/versoes/comparar/${selA}/${selB}`);
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(`/propostas/${id}`)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar para o detalhe
      </button>

      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Versões — {proposta.codigo}</h1>
        <Button
          variant="secondary"
          disabled={!selA || !selB || selA === selB}
          onClick={compararSelecionadas}
        >
          <Scale className="mr-2 h-4 w-4" />
          Comparar
        </Button>
      </div>

      <div className="mb-3 flex items-center gap-4 text-sm text-gray-600">
        <span>Selecione duas versões para comparar (diff estruturado).</span>
      </div>

      <ul className="space-y-2">
        {versoes.map((v) => {
          const vigente = v.id === proposta.currentVersionId;
          const checkedA = selA === v.id;
          const checkedB = selB === v.id;
          return (
            <li
              key={v.id}
              className="flex items-center gap-3 rounded border border-gray-200 bg-white p-3 text-sm"
            >
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={checkedA}
                    onChange={(e) => setSelA(e.target.checked ? v.id : '')}
                    className="h-3 w-3"
                  />
                  A
                </label>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={checkedB}
                    onChange={(e) => setSelB(e.target.checked ? v.id : '')}
                    className="h-3 w-3"
                  />
                  B
                </label>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">v{v.versionNumber}</span>
                  {vigente && (
                    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                      Vigente
                    </span>
                  )}
                  <StatusBadge status={v.status} />
                </div>
                <div className="mt-0.5 text-xs text-gray-500">
                  Criada em {new Date(v.createdAt).toLocaleString('pt-BR')} · Valor{' '}
                  {formatCurrency(v.valorTotal)}
                </div>
                {v.motivoAlteracao && (
                  <p className="mt-1 text-xs text-gray-600">Motivo: {v.motivoAlteracao}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export { PropostaVersoesPage };
